import React from 'react';
import SearchableSelect from '../shared/SearchableSelect';
import { useBodyScrollLock } from '../../lib/useBodyScrollLock';
import { usePath, navigate, parseRoute } from '../../lib/router';
import ReactDOM from 'react-dom';
import { auth } from '../../lib/firebase';
import {
  Search, Filter, Loader, TicketCheck, User, CalendarDays, ArrowUpDown, Plus,
  Send, X, Archive, RotateCcw, Bell, BellRing, MessageSquare, Mail,
  Volume2, MoreHorizontal, Eye, CheckCheck, Clock, AlertTriangle,
  UserPlus, Flag, RefreshCcw, Paperclip, ShieldCheck, Keyboard,
  PanelLeftClose, PanelLeftOpen, Settings, Pin, PinOff,
} from 'lucide-react';
import TaskNotificationCenter from '../shared/TaskNotificationCenter';
import AdminTicketThread from './AdminTicketThread';
import TicketSplitView, { StatusPill, PriorityPill } from './TicketSplitView';
import TicketSidebar, { TicketSectionStrip, TICKET_SECTION_LABELS, type TicketSection } from './TicketSidebar';
import SlaBadge from './tickets/SlaBadge';

/** Minimum comfortable width for the conversation column. Below this the
 *  details panel collapses first — the conversation is the primary workspace. */
const CHAT_MIN_PX = 480;
import Modal from '../shared/Modal';
import { ParticipantAvatar } from '../shared/TicketChatComponents';
import { useModalLayer } from '../../lib/modalStack';
import { emailTicketResolved } from '../../lib/emailTriggers';
import { workspaceHeader } from '../../lib/workspaces';
import type { Ticket, TicketComment, TicketStatus, TicketPriority, Application } from '../../types';
import ImproveTextDropdown from '../shared/ImproveTextDropdown';
import {
  ToastContainer, showTicketToast, NotificationCenter,
  SoundPrefsPanel, useUnreadCount, sendBrowserNotification,
} from './TicketNotifications';

/**
 * Is this ticket worked by `uid`? MULTI-ASSIGNEE aware — the owner OR any
 * collaborator. Every "mine"-style count and filter goes through this, so an
 * agent added as a COLLABORATOR still finds the ticket in "My tickets" instead
 * of it silently disappearing for everyone but the owner.
 */
function isTicketAssignedTo(ticket: Ticket, uid?: string | null): boolean {
  if (!uid) return false;
  if (ticket.assignedTo === uid) return true;
  return Array.isArray(ticket.assignees) && ticket.assignees.some(a => a?.uid === uid);
}

const STATUS_LABELS: Record<TicketStatus, string> = {
  open: 'Open', in_progress: 'In Progress', pending: 'Pending', resolved: 'Resolved', closed: 'Closed',
};

const STATUS_COLORS: Record<TicketStatus, string> = {
  open: 'bg-brand-active border-blue-200',
  in_progress: 'bg-amber-50 text-amber-700 border-amber-200',
  pending: 'bg-purple-50 text-purple-700 border-purple-200',
  resolved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  closed: 'bg-slate-100 text-slate-500 border-slate-200',
};

const PRIORITY_ORDER: Record<TicketPriority, number> = { urgent: 0, high: 1, medium: 2, low: 3 };

function formatLastActivity(iso: string): string {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
  return new Date(iso).toLocaleDateString();
}

function getTicketInitials(name?: string): string {
  if (!name) return '?';
  return name.trim().split(/\s+/).slice(0, 2).map(p => p.charAt(0).toUpperCase()).join('') || '?';
}

function FilterChip({ label, onClear }: { label: string; onClear: () => void }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-blue-50 border border-blue-200 text-brand text-[10px] font-bold rounded-lg">
      {label}
      <button onClick={onClear} className="text-brand/70 hover:text-brand cursor-pointer" type="button" aria-label={`Clear filter: ${label}`}>
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

interface TicketsPageProps {
  applications: any[];
  programsList?: { id: string; name: string }[];
  allAdmins?: { uid: string; name: string; email: string }[];
  appCtx?: any;
  currentUserProfile?: any;
  canEdit?: boolean;
}

export default function TicketsPage({ applications, programsList, allAdmins, appCtx, currentUserProfile, canEdit = true }: TicketsPageProps) {
  const [tickets, setTickets] = React.useState<Ticket[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [selectedTicket, setSelectedTicket] = React.useState<Ticket | null>(null);
  const [selectedTicketComments, setSelectedTicketComments] = React.useState<TicketComment[]>([]);

  /* ── Responsive details panel (design spec) ──────────────────────────────
   *   ≥1400px  → all three columns share the grid
   *   1100–1399px → details auto-collapses (the Details button re-opens it)
   *   <1100px  → details becomes an overlay drawer so it never competes with
   *              the composer for width
   * The composer's minimum width is treated as sacred: side columns give way,
   * never the compose bar.
   */
  const [viewportWidth, setViewportWidth] = React.useState(() =>
    typeof window === 'undefined' ? 1600 : window.innerWidth);
  React.useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const currentPath = usePath();
  const currentRoute = parseRoute(currentPath);

  // Sync selectedTicket with URL path - only run when route changes, not on every ticket update
  React.useEffect(() => {
    if (currentRoute.type === 'tickets_detail') {
      const targetId = currentRoute.id;
      // Only attempt to open if we don't already have this ticket selected
      if (selectedTicket?.ticketId !== targetId && targetId) {
        const found = tickets.find(t => t.ticketId === targetId);
        if (found) {
          openTicket(found);
        }
      }
    } else if (currentRoute.type === 'base' && currentRoute.section === 'tickets' && selectedTicket) {
      setSelectedTicket(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRoute.type, currentRoute.id]);

  const prevSelectedTicketRef = React.useRef<string | null>(selectedTicket?.ticketId || null);

  // Sync URL path when selectedTicket changes - stable navigation without re-triggering effects
  React.useEffect(() => {
    const newId = selectedTicket?.ticketId || null;
    if (newId && prevSelectedTicketRef.current !== newId) {
      navigate(`/tickets/adminview/${newId}`);
      prevSelectedTicketRef.current = newId;
    } else if (!newId && prevSelectedTicketRef.current && currentRoute.type === 'tickets_detail') {
      navigate('/tickets');
      prevSelectedTicketRef.current = null;
    }
  }, [selectedTicket?.ticketId, currentRoute.type]);
  const [showCreate, setShowCreate] = React.useState(false);
  const createZ = useModalLayer(showCreate);
  const [newTitle, setNewTitle] = React.useState('');
  const [newDescription, setNewDescription] = React.useState('');
  const [newAppId, setNewAppId] = React.useState('');
  const [newPriority, setNewPriority] = React.useState<TicketPriority>('medium');
  const [newAssignee, setNewAssignee] = React.useState('');
  const [appSearch, setAppSearch] = React.useState('');
  const [creating, setCreating] = React.useState(false);

  // Filters
  const [search, setSearch] = React.useState('');
  const [filterStatus, setFilterStatus] = React.useState<TicketStatus | ''>('');
  const [filterPriority, setFilterPriority] = React.useState<TicketPriority | ''>('');
  const [assignedFilter, setAssignedFilter] = React.useState<'all' | 'unassigned' | 'me'>('all');
  const [sortBy, setSortBy] = React.useState<'newest' | 'oldest' | 'priority'>('newest');
  const [filterLastReply, setFilterLastReply] = React.useState<'all' | 'applicant'>('all');
  const [programFilter, setProgramFilter] = React.useState('');
  const [createdAfter, setCreatedAfter] = React.useState('');
  const [createdBefore, setCreatedBefore] = React.useState('');
  const [activityRange, setActivityRange] = React.useState<'all' | '24h' | '7d' | '30d'>('all');
  const [showArchived, setShowArchived] = React.useState(false);
  const [section, setSection] = React.useState<TicketSection>('all');
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false);
  const [leftListCollapsed, setLeftListCollapsed] = React.useState(false);
  const [rightDetailsCollapsed, setRightDetailsCollapsed] = React.useState(false);
  /**
   * Measured width of the workspace, and the ONLY input to layout decisions.
   *
   * `window.innerWidth` was the previous input and it is the wrong one: opening
   * the app sidebar (or collapsing the Ticket Views rail, or the ticket list)
   * takes width away from this workspace WITHOUT changing the viewport. The
   * viewport-based breakpoints therefore kept the three-column template
   * (list + CHAT_MIN + details) alive after the sidebar opened, the tracks summed
   * to more than the container, and the grid clipped its last column — the ticket
   * /applicant details panel slid off the right edge.
   *
   * Measuring the container makes every collapse of a sibling panel reflow the
   * workspace, and the observer is attached with a CALLBACK REF because the
   * workspace only renders once a ticket is selected (an effect with `[]` deps
   * ran before the node existed, observed nothing, and left the width at 0 — so
   * the fit check never fired at all).
   */
  const [workspaceWidth, setWorkspaceWidth] = React.useState(0);
  const workspaceObserverRef = React.useRef<ResizeObserver | null>(null);
  const attachWorkspace = React.useCallback((el: HTMLDivElement | null) => {
    workspaceObserverRef.current?.disconnect();
    workspaceObserverRef.current = null;
    if (!el) return;
    const measure = (next: number) => {
      if (!next) return;
      setWorkspaceWidth(prev => (Math.abs(prev - next) > 1 ? next : prev));
    };
    measure(el.getBoundingClientRect().width);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(entries => measure(entries[0]?.contentRect?.width || 0));
    ro.observe(el);
    workspaceObserverRef.current = ro;
  }, []);
  React.useEffect(() => () => workspaceObserverRef.current?.disconnect(), []);

  // Shell allowance (app sidebar + page gutters) used only until the workspace
  // has been measured, so the first paint already picks a sane layout.
  const layoutWidth = workspaceWidth > 0 ? workspaceWidth : Math.max(0, viewportWidth - 264);

  /* Where the details panel lives, derived from the MEASURED width:
   *   ≥1240px           → resizable third column
   *   1024–1239px       → 48px rail + overlay drawer, so it never competes for width
   *   <1024px           → overlay drawer only
   * 1240px is the sum of the panel's real needs: list ≤300 + gap 32 + conversation
   * (CHAT_MIN_PX) + gap 32 + details ≥396. Below that the panel stops being a
   * column hero and becomes a drawer, instead of squeezing the conversation.
   */
  const DETAILS_INLINE_MIN_PX = 300 + 32 + CHAT_MIN_PX + 32 + 396;
  const DETAILS_RAIL_MIN_PX = 1024;
  const detailsOverlay = layoutWidth < DETAILS_RAIL_MIN_PX;
  const detailsRail = layoutWidth >= DETAILS_RAIL_MIN_PX && layoutWidth < DETAILS_INLINE_MIN_PX;
  const detailsInGrid = !rightDetailsCollapsed && !detailsOverlay && !detailsRail;
  const detailsDrawer = !rightDetailsCollapsed && (detailsOverlay || detailsRail);

  // Default follows the layout; an explicit toggle wins until the workspace
  // crosses the inline breakpoint, where auto behaviour sensibly takes over again.
  const userToggledDetailsRef = React.useRef(false);
  const detailsFitInline = layoutWidth >= DETAILS_INLINE_MIN_PX;
  React.useEffect(() => {
    userToggledDetailsRef.current = false;
    setRightDetailsCollapsed(!detailsFitInline);
  }, [detailsFitInline]);

  // ── Details panel width (drag to resize, remembered per browser) ─────────
  const DETAILS_WIDTH_KEY = 'isats:ticket-details-width';
  const clampDetailsWidth = (w: number) => Math.min(560, Math.max(300, w));
  const [detailsWidth, setDetailsWidth] = React.useState<number>(() => {
    if (typeof window === 'undefined') return 360;
    try {
      const stored = Number(window.localStorage.getItem(DETAILS_WIDTH_KEY));
      return Number.isFinite(stored) && stored > 0 ? clampDetailsWidth(stored) : 360;
    } catch { return 360; }
  });
  const detailsResizeRef = React.useRef<{ startX: number; startWidth: number } | null>(null);

  // Collapsed list → a 36px rail. The TRACK must shrink with it, otherwise the
  // space is reserved but painted empty (the "huge blank area" report). 240–300px
  // is enough for a title, status dot and timestamp at a comfortable density.
  const listTrack = leftListCollapsed ? '36px' : 'minmax(240px, 300px)';

  // The inline panel is capped by the room that is genuinely left after the list
  // and the conversation's minimum width. Dragging the resize handle can
  // therefore never push the conversation below CHAT_MIN_PX — past that point the
  // panel simply stops growing, which is the behaviour a split view should have.
  // Declared BEFORE `workspaceColumns` reads it (TDZ-safe ordering).
  const inlineDetailsWidth = Math.max(
    320,
    Math.min(detailsWidth, layoutWidth - 300 - 32 - CHAT_MIN_PX - 32),
  );

  /**
   * Single source of truth for the column template — declared AFTER detailsWidth
   * so the `detailsInGrid` branch can read it (TDZ-safe ordering).
   *
   * Applied as an INLINE STYLE, never as a Tailwind arbitrary class: Tailwind's
   * JIT scans source text for complete class strings, so a class name assembled
   * from a variable emits no CSS at all.
   *
   *  <768px → one column (the list is hidden; details becomes a drawer)
   *  details in grid / rail / hidden → 3 / 3 / 2 tracks
   *
   * The conversation track is `minmax(0, 1fr)`, NOT `minmax(CHAT_MIN_PX, 1fr)`.
   * A hard minimum the container cannot satisfy is exactly what pushes the last
   * track out of the grid and off the right edge; the CHAT_MIN_PX floor is now
   * enforced by the breakpoint decision above (which turns the panel into a
   * drawer or collapses it) so the template itself can never overflow.
   */
  const workspaceColumns = layoutWidth < 768
    ? 'minmax(0, 1fr)'
    : detailsInGrid
      ? `${listTrack} minmax(0, 1fr) ${inlineDetailsWidth}px`
      : detailsRail
        ? `${listTrack} minmax(0, 1fr) 48px`
        : `${listTrack} minmax(0, 1fr)`;

  const handleDetailsResizeStart = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    detailsResizeRef.current = { startX: e.clientX, startWidth: detailsWidth };
    const onMove = (ev: MouseEvent) => {
      const drag = detailsResizeRef.current;
      if (!drag) return;
      // Dragging the handle left widens the panel, so the chat column gives up
      // room only when the admin deliberately asks it to.
      setDetailsWidth(Math.min(560, Math.max(300, drag.startWidth + (drag.startX - ev.clientX))));
    };
    const onUp = () => {
      detailsResizeRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [detailsWidth]);

  React.useEffect(() => {
    try { window.localStorage.setItem(DETAILS_WIDTH_KEY, String(detailsWidth)); } catch { /* best-effort */ }
  }, [detailsWidth]);
  const [showFiltersPopover, setShowFiltersPopover] = React.useState(false);
  const filtersPopoverRef = React.useRef<HTMLDivElement>(null);
  const moreFiltersBtnRef = React.useRef<HTMLButtonElement>(null);
  const [filtersPos, setFiltersPos] = React.useState<{ top: number; left: number } | null>(null);
  const [archivingId, setArchivingId] = React.useState<string | null>(null);
  const [refreshing, setRefreshing] = React.useState(false);
  const [showShortcuts, setShowShortcuts] = React.useState(false);

  // ── Applicant picker popover (New Ticket modal) ──────────────────────────
  // The results list used to live inline inside the modal's scroll container,
  // so it was clipped at the modal edge and fought the modal's own scrollbar.
  // It is portalled to <body> and anchored to the input instead — the same
  // pattern the row action popovers use — so it can never be clipped.
  const appSearchRef = React.useRef<HTMLInputElement | null>(null);
  const [appSearchOpen, setAppSearchOpen] = React.useState(false);
  const [appSearchRect, setAppSearchRect] = React.useState<DOMRect | null>(null);

  const syncAppSearchRect = React.useCallback(() => {
    const el = appSearchRef.current;
    if (el) setAppSearchRect(el.getBoundingClientRect());
  }, []);

  React.useEffect(() => {
    if (!appSearchOpen) return;
    syncAppSearchRect();
    window.addEventListener('resize', syncAppSearchRect);
    window.addEventListener('scroll', syncAppSearchRect, true);
    return () => {
      window.removeEventListener('resize', syncAppSearchRect);
      window.removeEventListener('scroll', syncAppSearchRect, true);
    };
  }, [appSearchOpen, syncAppSearchRect]);
  const [visibleLimit, setVisibleLimit] = React.useState(50);

  // Reset the pagination cap whenever the active view/filters change
  React.useEffect(() => {
    setVisibleLimit(50);
  }, [section, search, filterStatus, filterPriority, assignedFilter, filterLastReply, programFilter, createdAfter, createdBefore, activityRange, showArchived]);
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const [bulkAction, setBulkAction] = React.useState<'assign' | 'status' | 'priority' | 'archive' | null>(null);

  // Notification state — persisted to sessionStorage (key refreshes on auth change)
  const [showNotifications, setShowNotifications] = React.useState(false);
  const [showSoundPrefs, setShowSoundPrefs] = React.useState(false);
  const [notifications, setNotifications] = React.useState<import('./TicketNotifications').AdminNotification[]>([]);
  const notifKeyRef = React.useRef(`ticket_notifs_${auth.currentUser?.uid || 'anon'}`);
  // Reload notifications when auth state changes
  React.useEffect(() => {
    const unsub = auth.onAuthStateChanged((user) => {
      const key = `ticket_notifs_${user?.uid || 'anon'}`;
      notifKeyRef.current = key;
      try {
        const saved = sessionStorage.getItem(key);
        setNotifications(saved ? JSON.parse(saved).slice(0, 50) : []);
      } catch { setNotifications([]); }
    });
    // Initial load
    try {
      const saved = sessionStorage.getItem(notifKeyRef.current);
      if (saved) setNotifications(JSON.parse(saved).slice(0, 50));
    } catch { /* ignore */ }
    return unsub;
  }, []);

  // Persist notifications to sessionStorage
  React.useEffect(() => {
    try { sessionStorage.setItem(notifKeyRef.current, JSON.stringify(notifications.slice(0, 50))); } catch { /* ignore */ }
  }, [notifications]);
  const [activeQuickAction, setActiveQuickAction] = React.useState<string | null>(null);

  // Track previous tickets for detecting new messages
  const prevTicketsRef = React.useRef<Ticket[]>([]);
  const soundPrefsRef = React.useRef(loadSoundPrefs());

  // Load sound prefs
  function loadSoundPrefs() {
    try {
      const raw = localStorage.getItem('ticket_sound_prefs');
      return raw ? JSON.parse(raw) : { enabled: false };
    } catch { return { enabled: false }; }
  }

  // Mark ticket as read
  const markTicketRead = React.useCallback(async (ticketId: string) => {
    try {
      const token = await getTokenOrRedirect();
      if (!token) return;
      await fetch(`/api/tickets/${ticketId}/read`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, ...workspaceHeader() },
      });
    } catch { /* ignore */ }
  }, []);

  // Redirect to login if session has expired
  const getTokenOrRedirect = React.useCallback(async (): Promise<string | null> => {
    const user = auth.currentUser;
    if (!user) {
      // Session expired — redirect to login
      window.location.href = '/login';
      return null;
    }
    try {
      return await user.getIdToken();
    } catch {
      window.location.href = '/login';
      return null;
    }
  }, []);

  const fetchTickets = React.useCallback(async (includeArchived = false) => {
    setRefreshing(true);
    try {
      const token = await getTokenOrRedirect();
      if (!token) return;
      const res = await fetch(`/api/tickets${includeArchived ? '?includeArchived=true' : ''}`, {
        headers: { Authorization: `Bearer ${token}`, ...workspaceHeader() },
      });
      const data = await res.json();
      const newTickets = (data.tickets || []) as Ticket[];

      // Detect new applicant messages for toast + sound
      const prev = prevTicketsRef.current;
      if (prev.length > 0) {
        for (const ticket of newTickets) {
          const old = prev.find(t => t.ticketId === ticket.ticketId);
          if (old && ticket.unread && !old.unread) {
            // New unread message from applicant
            const prefs = loadSoundPrefs();
            showTicketToast({
              type: 'new_message',
              title: ticket.title,
              body: ticket.lastCommentBody || 'New message received',
              ticketId: ticket.ticketId,
              senderName: ticket.lastCommentAuthor || ticket.applicantName,
              createdAt: ticket.lastCommentAt || new Date().toISOString(),
              onOpen: (tid: string) => {
                const found = newTickets.find(t => t.ticketId === tid);
                if (found) openTicket(found);
              },
            }, prefs);

            // Browser notification if tab is not focused
            if (document.visibilityState !== 'visible') {
              sendBrowserNotification(
                `${ticket.applicantName} replied to ${ticket.title}`,
                { body: ticket.lastCommentBody?.slice(0, 120) || 'New message' },
              );
            }

            // Add to notification center
            setNotifications(prevN => [{
              id: `notif_${ticket.ticketId}_${Date.now()}`,
              type: 'new_message' as const,
              ticketId: ticket.ticketId,
              title: ticket.title,
              body: ticket.lastCommentBody || 'New message',
              senderName: ticket.lastCommentAuthor || ticket.applicantName,
              createdAt: new Date().toISOString(),
              read: false,
            }, ...prevN].slice(0, 100));
          }
        }
      }
      // Stable polling merge: only update tickets that actually changed
      prevTicketsRef.current = newTickets;
      setTickets(prev => {
        const merged = [...prev];
        let changed = false;
        for (const t of newTickets) {
          const idx = merged.findIndex(m => m.ticketId === t.ticketId);
          const old = idx >= 0 ? merged[idx] : null;
          if (!old) { merged.push(t); changed = true; }
          else if (old.updatedAt !== t.updatedAt || old.unreadCount !== t.unreadCount || old.status !== t.status) {
            merged[idx] = t; changed = true;
          }
        }
        return changed ? merged : prev;
      });
    } catch (err) {
      console.error('Failed to fetch tickets:', err);
    }
    setRefreshing(false);
    setLoading(false);
  }, []);

  // Initial load and polling (pauses when tab is hidden)
  React.useEffect(() => {
    fetchTickets(showArchived);
    let interval: ReturnType<typeof setInterval>;
    const startPolling = () => {
      clearInterval(interval);
      interval = setInterval(() => fetchTickets(showArchived), 6000);
    };
    startPolling();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') startPolling();
      else clearInterval(interval);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => { clearInterval(interval); document.removeEventListener('visibilitychange', onVisibility); };
  }, [fetchTickets, showArchived]);

  // Refresh sound prefs reference
  React.useEffect(() => {
    const handler = () => { soundPrefsRef.current = loadSoundPrefs(); };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const handleCreateTicket = async () => {
    if (!newTitle.trim() || !newDescription.trim() || !newAppId) return;
    setCreating(true);
    try {
      const token = await getTokenOrRedirect();
      if (!token) return;
      const body: any = {
        applicationId: newAppId,
        title: newTitle.trim(),
        description: newDescription.trim(),
        priority: newPriority,
      };
      if (newAssignee) {
        const [assignUid, assignName] = newAssignee.split('|');
        body.assignedTo = assignUid;
        body.assignedToName = assignName;
      }
      const res = await fetch('/api/tickets', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...workspaceHeader(),
        },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.ticket) {
        setTickets(prev => [data.ticket, ...prev]);
        setShowCreate(false);
        setNewTitle('');
        setNewDescription('');
        setNewAppId('');
        setNewPriority('medium');
        setNewAssignee('');
        setAppSearch('');

        // T-1 removed: server-side POST /api/tickets now handles the
        // applicant notification for both admin-created and applicant-created
        // tickets. The frontend was a duplicate.
      }
    } catch (err: any) {
      showTicketToast({
        type: 'system',
        title: 'Failed to create ticket',
        body: err.message || 'Please try again.',
        createdAt: new Date().toISOString(),
      });
    }
    setCreating(false);
  };

  const filteredApplications = React.useMemo(() => {
    if (!appSearch) return applications.slice(0, 50);
    const q = appSearch.toLowerCase();
    return applications.filter((a: any) =>
      (a.userEmail || '').toLowerCase().includes(q) ||
      (a.personalDetails?.firstName || '').toLowerCase().includes(q) ||
      (a.personalDetails?.lastName || '').toLowerCase().includes(q) ||
      (a.applicationId || '').toLowerCase().includes(q)
    ).slice(0, 50);
  }, [applications, appSearch]);

  // Guards against out-of-order responses when an agent clicks through tickets
  // quickly: only the most recent request may write to state.
  const openRequestRef = React.useRef(0);
  const selectedTicketRef = React.useRef<Ticket | null>(null);
  selectedTicketRef.current = selectedTicket;

  const openTicket = React.useCallback(async (t: Ticket) => {
    const requestId = ++openRequestRef.current;
    setSelectedTicket(t);
    setSelectedTicketComments([]);
    // Mark as read immediately on open
    markTicketRead(t.ticketId);
    setTickets(prev => prev.map(tk =>
      tk.ticketId === t.ticketId ? { ...tk, unread: false, unreadCount: 0 } : tk,
    ));
    // This fetch feeds the context rail (timeline + counts). The thread
    // itself loads its own stream via the shared engine (Chat Phase 3),
    // which owns its loading/error/retry states.
    try {
      const token = await getTokenOrRedirect();
      if (!token) return;
      const res = await fetch(`/api/tickets/${t.ticketId}`, {
        headers: { Authorization: `Bearer ${token}`, ...workspaceHeader() },
      });
      if (!res.ok) throw new Error(`Could not load this conversation (error ${res.status}).`);
      const data = await res.json();
      if (openRequestRef.current !== requestId) return; // a newer ticket won
      setSelectedTicketComments(data.comments || []);
    } catch {
      if (openRequestRef.current !== requestId) return;
      setSelectedTicketComments([]);
    }
  }, [markTicketRead]);

  const handleUpdate = React.useCallback(async (ticketId: string, updates: Partial<Ticket>) => {
    try {
      const token = await getTokenOrRedirect();
      if (!token) return;
      const res = await fetch(`/api/tickets/${ticketId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...workspaceHeader(),
        },
        body: JSON.stringify(updates),
      });
      const data = await res.json();
      if (data.ticket) {
        setTickets(prev => prev.map(t => t.ticketId === ticketId ? { ...t, ...data.ticket } : t));
        setSelectedTicket(prev => prev && prev.ticketId === ticketId ? { ...prev, ...data.ticket } : null);

        // T-3 / T-4: Notify applicant on status change
        if (updates.status) {
          const ticket = data.ticket;
          const relatedApp = applications.find((a: any) => a.applicationId === ticket.applicationId);
          if (relatedApp) {
            const isResolved = updates.status === 'resolved' || updates.status === 'closed';
            if (isResolved) {
              emailTicketResolved(relatedApp, ticket.title).catch(() => {});
            } else {
              const { emailTicketStatusChanged } = await import('../../lib/emailTriggers');
              emailTicketStatusChanged(relatedApp, ticket.title, updates.status).catch(() => {});
            }
          }
        }
      }
      // Only refresh comments for status changes when we have an active selected ticket
      if (updates.status && selectedTicket?.ticketId === ticketId) {
        // Fetch fresh comments without re-setting the entire selected ticket
        try {
          const token = await getTokenOrRedirect();
          if (!token) return;
          const res = await fetch(`/api/tickets/${ticketId}`, {
            headers: { Authorization: `Bearer ${token}`, ...workspaceHeader() },
          });
          const data = await res.json();
          // Re-check after the await — the agent may have switched tickets.
          if (selectedTicketRef.current?.ticketId !== ticketId) return;
          setSelectedTicketComments(data.comments || []);
        } catch { /* ignore */ }
      }
    } catch (err) {
      console.error('Update failed:', err);
    }
  }, [applications, selectedTicket?.ticketId]);

  /* Chat Phase 3: the converged thread (AdminTicketThread → shared engine)
     persists its own mutations (title edit, reopen) and reports them here
     for a local merge — unlike handleUpdate this must NOT re-PATCH. */
  const mergeTicketUpdate = React.useCallback((ticketId: string, updates: Partial<Ticket>) => {
    setTickets(prev => prev.map(t => (t.ticketId === ticketId ? { ...t, ...updates } : t)));
    setSelectedTicket(prev => (prev && prev.ticketId === ticketId ? { ...prev, ...updates } : prev));
  }, []);

  // ── Sidebar section navigation ──
  const handleSectionChange = React.useCallback((s: TicketSection) => {
    setSection(s);
    if (s === 'archived') setShowArchived(true);
    else if (showArchived) setShowArchived(false);
  }, [showArchived]);

  const programOptions = React.useMemo(() => {
    const set = new Set<string>();
    tickets.forEach(t => { if (t.programName) set.add(t.programName); });
    return Array.from(set).sort();
  }, [tickets]);

  // Applicant profile photos, keyed by both applicationId and userId so any
  // ticket can resolve a photo regardless of which identifier it carries.
  // TWO namespaces, deliberately.
  //
  // This used to be one flat map keyed by applicationId + userId + `a.id`. Any
  // collision between those values let one applicant's photo overwrite another's
  // entry, so a ticket could render the WRONG person's photo — the
  // "mismatched avatar" that kept getting flagged. Application lookups and user
  // lookups now live in separate maps and can never cross-bind.
  const applicantPhotos = React.useMemo(() => {
    const byApplication: Record<string, string> = {};
    const byUser: Record<string, string> = {};
    (applications || []).forEach((a: any) => {
      const url = a?.profilePhotoUrl || a?.photoUrl
        || a?.personalDetails?.profilePhotoUrl || a?.personalDetails?.profilePhoto;
      if (typeof url !== 'string' || !(url.startsWith('http') || url.startsWith('data:'))) return;
      if (a.applicationId) byApplication[a.applicationId] = url;
      // `a.id` is the application doc id in this codebase — it belongs in the
      // APPLICATION namespace and must never be used as a user key.
      if (a.id) byApplication[a.id] = url;
      if (a.userId) byUser[a.userId] = url;
    });
    return { byApplication, byUser };
  }, [applications]);

  const ticketPhoto = (t: Ticket): string | undefined =>
    applicantPhotos.byApplication[t.applicationId] || applicantPhotos.byUser[t.applicantId];

  const hasActiveFilters = section !== 'all' || !!filterStatus || !!filterPriority
    || assignedFilter !== 'all' || filterLastReply !== 'all' || !!programFilter
    || !!createdAfter || !!createdBefore || activityRange !== 'all' || (showArchived && section === 'all');

  const clearAllFilters = () => {
    setSection('all');
    setFilterStatus('');
    setFilterPriority('');
    setAssignedFilter('all');
    setFilterLastReply('all');
    setShowArchived(false);
    setProgramFilter('');
    setCreatedAfter('');
    setCreatedBefore('');
    setActivityRange('all');
  };

  // ── Open the applicant in the one workspace via parent AdminDashboard
  // (ADM-13: no preview modal; origin recorded as the tickets surface) ──
  const handleViewApplicant = React.useCallback(async (applicantId: string) => {
    // Find the matching application from the list. Callers may pass the
    // applicant's Firebase UID (TicketDetailModal) or the application doc id
    // (TicketSplitView), so match both shapes.
    const app = (applications || []).find((a: any) =>
      a.applicationId === applicantId
      || a.id === applicantId
      || a.userId === applicantId
      || a.applicationUser?.uid === applicantId
    );
    if (app && appCtx?.viewApplicantInWorkspace) {
      appCtx.viewApplicantInWorkspace(app, { tab: 'tickets' });
    }
  }, [applications, appCtx]);

  // Live counts + unread counts per sidebar section (non-archived sections exclude archived)
  const sectionCounts = React.useMemo(() => {
    const counts: Record<TicketSection, number> = {
      all: 0, unassigned: 0, me: 0, open: 0, in_progress: 0, pending: 0,
      waiting_applicant: 0, waiting_staff: 0, resolved: 0, closed: 0, archived: 0, high_priority: 0,
    };
    const uid = auth.currentUser?.uid;
    for (const t of tickets) {
      if (t.archived) { counts.archived++; continue; }
      counts.all++;
      if (!t.assignedTo) counts.unassigned++;
      if (isTicketAssignedTo(t, uid)) counts.me++;
      if (t.status === 'open') counts.open++;
      if (t.status === 'in_progress') counts.in_progress++;
      if (t.status === 'pending') counts.pending++;
      if (t.lastCommentBy === 'admin') counts.waiting_applicant++;
      if (t.lastCommentBy === 'applicant') counts.waiting_staff++;
      if (t.status === 'resolved') counts.resolved++;
      if (t.status === 'closed') counts.closed++;
      if (t.priority === 'urgent' || t.priority === 'high') counts.high_priority++;
    }
    return counts;
  }, [tickets]);

  const unreadSectionCounts = React.useMemo(() => {
    const counts: Record<TicketSection, number> = {
      all: 0, unassigned: 0, me: 0, open: 0, in_progress: 0, pending: 0,
      waiting_applicant: 0, waiting_staff: 0, resolved: 0, closed: 0, archived: 0, high_priority: 0,
    };
    const uid = auth.currentUser?.uid;
    for (const t of tickets) {
      if (!t.unread) continue;
      if (t.archived) { counts.archived++; continue; }
      counts.all++;
      if (!t.assignedTo) counts.unassigned++;
      if (isTicketAssignedTo(t, uid)) counts.me++;
      if (t.status === 'open') counts.open++;
      if (t.status === 'in_progress') counts.in_progress++;
      if (t.status === 'pending') counts.pending++;
      if (t.lastCommentBy === 'admin') counts.waiting_applicant++;
      if (t.lastCommentBy === 'applicant') counts.waiting_staff++;
      if (t.status === 'resolved') counts.resolved++;
      if (t.status === 'closed') counts.closed++;
      if (t.priority === 'urgent' || t.priority === 'high') counts.high_priority++;
    }
    return counts;
  }, [tickets]);

  // Filtering + sorting
  const filtered = React.useMemo(() => {
    let result = [...tickets];

    // Sidebar section acts as the primary navigation. Archived tickets are
    // only surfaced via the "Archived" section or the explicit toggle.
    if (section === 'archived') {
      result = result.filter(t => t.archived);
    } else {
      result = result.filter(t => !t.archived);
      if (section === 'unassigned') result = result.filter(t => !t.assignedTo);
      else if (section === 'me') result = result.filter(t => isTicketAssignedTo(t, auth.currentUser?.uid));
      else if (section === 'open') result = result.filter(t => t.status === 'open');
      else if (section === 'in_progress') result = result.filter(t => t.status === 'in_progress');
      else if (section === 'pending') result = result.filter(t => t.status === 'pending');
      else if (section === 'waiting_applicant') result = result.filter(t => t.lastCommentBy === 'admin');
      else if (section === 'waiting_staff') result = result.filter(t => t.lastCommentBy === 'applicant');
      else if (section === 'resolved') result = result.filter(t => t.status === 'resolved');
      else if (section === 'closed') result = result.filter(t => t.status === 'closed');
      else if (section === 'high_priority') result = result.filter(t => t.priority === 'urgent' || t.priority === 'high');
    }
    // "all" + explicit archived toggle shows archived alongside everything else
    if (section === 'all' && showArchived) {
      result = tickets.filter(() => true);
    }

    if (search) {
      const q = search.toLowerCase();
      result = result.filter(t =>
        t.title.toLowerCase().includes(q) ||
        t.applicantName.toLowerCase().includes(q) ||
        t.applicantEmail.toLowerCase().includes(q) ||
        t.ticketId.toLowerCase().includes(q)
      );
    }
    if (filterStatus) result = result.filter(t => t.status === filterStatus);
    if (filterPriority) result = result.filter(t => t.priority === filterPriority);
    if (assignedFilter === 'unassigned') result = result.filter(t => !t.assignedTo);
    if (assignedFilter === 'me') result = result.filter(t => isTicketAssignedTo(t, auth.currentUser?.uid));
    if (filterLastReply === 'applicant') result = result.filter(t => t.lastCommentBy === 'applicant');
    if (programFilter) result = result.filter(t => t.programName === programFilter);
    if (createdAfter) {
      const after = new Date(createdAfter).getTime();
      if (!isNaN(after)) result = result.filter(t => new Date(t.createdAt).getTime() >= after);
    }
    if (createdBefore) {
      const before = new Date(`${createdBefore}T23:59:59`).getTime();
      if (!isNaN(before)) result = result.filter(t => new Date(t.createdAt).getTime() <= before);
    }
    if (activityRange !== 'all') {
      const cutoffMs = activityRange === '24h' ? 86_400_000 : activityRange === '7d' ? 604_800_000 : 2_592_000_000;
      const cutoff = Date.now() - cutoffMs;
      result = result.filter(t => new Date(t.lastCommentAt || t.updatedAt || t.createdAt).getTime() >= cutoff);
    }

    result.sort((a, b) => {
      // Chat Phase 4: the viewer's pinned tickets always lead the list,
      // ahead of whichever ordering the agent picked.
      const pinDelta = (b.viewerPinned ? 1 : 0) - (a.viewerPinned ? 1 : 0);
      if (pinDelta !== 0) return pinDelta;
      if (sortBy === 'newest') return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      if (sortBy === 'oldest') return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      if (sortBy === 'priority') {
        const pa = PRIORITY_ORDER[a.priority] ?? 3;
        const pb = PRIORITY_ORDER[b.priority] ?? 3;
        return pa - pb;
      }
      return 0;
    });

    return result;
  }, [tickets, section, search, filterStatus, filterPriority, assignedFilter, sortBy, filterLastReply, showArchived, programFilter, createdAfter, createdBefore, activityRange]);

  // Pagination for large inboxes
  const pagedTickets = filtered.slice(0, visibleLimit);
  const hasMoreTickets = filtered.length > visibleLimit;
  const loadMoreTickets = () => setVisibleLimit(l => l + 50);

  // ── Keyboard shortcuts ──
  const currentTicketIdx = React.useMemo(
    () => (selectedTicket ? filtered.findIndex(t => t.ticketId === selectedTicket.ticketId) : -1),
    [filtered, selectedTicket?.ticketId],
  );

  const openPrevTicket = React.useCallback(() => {
    if (filtered.length === 0 || !openTicket) return;
    const idx = currentTicketIdx > 0 ? currentTicketIdx - 1 : filtered.length - 1;
    openTicket(filtered[idx]);
  }, [filtered, currentTicketIdx, openTicket]);

  const openNextTicket = React.useCallback(() => {
    if (filtered.length === 0 || !openTicket) return;
    const idx = currentTicketIdx >= 0 ? (currentTicketIdx + 1) % filtered.length : 0;
    openTicket(filtered[idx]);
  }, [filtered, currentTicketIdx, openTicket]);

  const searchInputRef = React.useRef<HTMLInputElement | null>(null);
  const focusTicketSearch = React.useCallback(() => {
    const el = searchInputRef.current;
    if (!el) return;
    el.focus();
    el.select();
  }, []);

  React.useEffect(() => {
    const isTyping = (target: EventTarget | null): boolean => {
      if (!(target instanceof HTMLElement)) return false;
      const tag = target.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
    };
    const handler = (e: KeyboardEvent) => {
      if (isTyping(e.target)) return;
      const hasModifier = e.metaKey || e.ctrlKey || e.altKey;
      // Esc tucks the details panel away wherever it lives — grid column, rail
      // drawer or overlay drawer. Open dialogs keep their own Esc handling, so
      // this defers to them rather than stealing the key.
      if (e.key === 'Escape' && !hasModifier && !rightDetailsCollapsed && !showCreate && !showShortcuts) {
        setRightDetailsCollapsed(true);
        return;
      }
      if (e.key === '/' && !hasModifier) { e.preventDefault(); focusTicketSearch(); return; }
      if (e.key === '?') { e.preventDefault(); setShowShortcuts(s => !s); }
      else if (e.key.toLowerCase() === 'n' && !hasModifier) { e.preventDefault(); setShowCreate(true); }
      else if (e.key === '[' || e.key.toLowerCase() === 'k') { e.preventDefault(); openPrevTicket(); }
      else if (e.key === ']' || e.key.toLowerCase() === 'j') { e.preventDefault(); openNextTicket(); }
      // Arrow keys only steer once a ticket is open, so they still scroll the
      // list normally when nothing is selected.
      else if (e.key === 'ArrowUp' && selectedTicket) { e.preventDefault(); openPrevTicket(); }
      else if (e.key === 'ArrowDown' && selectedTicket) { e.preventDefault(); openNextTicket(); }
      else if (e.key.toLowerCase() === 'r' && !hasModifier) { e.preventDefault(); fetchTickets(showArchived); }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [openPrevTicket, openNextTicket, fetchTickets, showArchived, selectedTicket, rightDetailsCollapsed, showCreate, showShortcuts, focusTicketSearch]);

  const handleBulkAction = async (action: string, value: string | boolean) => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    setBulkAction(action as any);
    try {
      const token = await getTokenOrRedirect();
      if (!token) return;
      if (action === 'archive') {
        await Promise.all(ids.map(id =>
          fetch(`/api/tickets/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...workspaceHeader() }, body: JSON.stringify({ archived: value }) })
        ));
        setTickets(prev => prev.map(t => ids.includes(t.ticketId) ? { ...t, archived: value as boolean } : t));
        showTicketToast({ type: 'system', title: `${ids.length} ticket${ids.length > 1 ? 's' : ''} ${value ? 'archived' : 'restored'}`, body: '', createdAt: new Date().toISOString() });
      } else {
        const updates = { [action]: value };
        await Promise.all(ids.map(id =>
          fetch(`/api/tickets/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...workspaceHeader() }, body: JSON.stringify(updates) })
        ));
        setTickets(prev => prev.map(t => ids.includes(t.ticketId) ? { ...t, ...updates } : t));
        showTicketToast({ type: 'system', title: `${ids.length} ticket${ids.length > 1 ? 's' : ''} updated`, body: `${action}: ${String(value)}`, createdAt: new Date().toISOString() });
      }
    } catch (err) {
      console.error('Bulk action failed:', err);
    }
    setBulkAction(null);
    setSelectedIds([]);
  };

  const handleArchive = async (ticketId: string, archived: boolean) => {
    setArchivingId(ticketId);
    try {
      const token = await getTokenOrRedirect();
      if (!token) return;
      const res = await fetch(`/api/tickets/${ticketId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ archived }),
      });
      const data = await res.json();
      if (data.ticket) {
        setTickets(prev => prev.map(t => t.ticketId === ticketId ? { ...t, ...data.ticket } : t));
      }
    } catch (err) {
      console.error('Archive toggle failed:', err);
    }
    setArchivingId(null);
  };

  /* Chat Phase 4: per-viewer pin. Personal triage only — the ticket's
     shared lifecycle archive above is untouched, and no other agent's
     list changes. Optimistic flip; a failed write rolls back + toasts. */
  const handleTogglePin = async (t: Ticket) => {
    const nextValue = !t.viewerPinned;
    setTickets(prev => prev.map(x => x.ticketId === t.ticketId ? { ...x, viewerPinned: nextValue } : x));
    try {
      const token = await getTokenOrRedirect();
      if (!token) throw new Error('no token');
      const res = await fetch(`/api/tickets/${t.ticketId}/state`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...workspaceHeader(),
        },
        body: JSON.stringify({ pinned: nextValue }),
      });
      if (!res.ok) throw new Error(`state ${res.status}`);
    } catch (err) {
      console.error('Pin toggle failed:', err);
      setTickets(prev => prev.map(x => x.ticketId === t.ticketId ? { ...x, viewerPinned: !nextValue } : x));
      showTicketToast({ type: 'system', title: 'Could not update pin', body: 'The change was not saved.', createdAt: new Date().toISOString() });
    }
  };

  const unreadCount = useUnreadCount(tickets);
  const openCount = tickets.filter(t => t.status === 'open' || t.status === 'in_progress').length;

  // Update tab title with unread count
  React.useEffect(() => {
    const name = (import.meta.env.VITE_BRAND_BUSINESS_NAME as string) || 'ISATS';
    document.title = unreadCount > 0 ? `(${unreadCount}) Support Tickets — ${name}` : `Support Tickets — ${name}`;
    return () => { document.title = `Support Tickets — ${name}`; };
  }, [unreadCount]);

  // Dismiss filters popover on outside click
  React.useEffect(() => {
    if (!showFiltersPopover) return;
    const handler = (e: MouseEvent) => {
      if (filtersPopoverRef.current && !filtersPopoverRef.current.contains(e.target as Node)) {
        setShowFiltersPopover(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showFiltersPopover]);

  // Dismiss activeQuickAction on outside click
  React.useEffect(() => {
    if (!activeQuickAction) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-quick-action]')) setActiveQuickAction(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [activeQuickAction]);

  // Keyboard shortcut: Cmd/Ctrl+N to create new ticket
  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'n' && !(e.target as HTMLElement)?.closest('input,textarea,select,[contenteditable]')) {
        e.preventDefault();
        setShowCreate(true);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Notification center handlers
  const handleMarkNotifRead = (id: string) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  };
  const handleMarkAllNotifRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };
  const handleDeleteNotif = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };
  const handleNotifOpenTicket = (ticketId: string) => {
    const found = tickets.find(t => t.ticketId === ticketId);
    if (found) { openTicket(found); setShowNotifications(false); }
  };

  // Inline action popovers
  const [actionPopover, setActionPopover] = React.useState<{
    type: 'assign' | 'priority' | 'status';
    ticket: Ticket;
  } | null>(null);
  const actionBtnRef = React.useRef<HTMLButtonElement | null>(null);
  const [actionBtnRect, setActionBtnRect] = React.useState<DOMRect | null>(null);

  const quickActions = (t: Ticket) => [
    { id: 'open', label: 'Open Ticket', icon: <Eye className="h-3.5 w-3.5" />, action: () => openTicket(t) },
    {
      id: 'pin', label: t.viewerPinned ? 'Unpin (just for you)' : 'Pin (just for you)',
      icon: t.viewerPinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />,
      action: () => void handleTogglePin(t),
    },
    {
      id: 'assign', label: 'Assign', icon: <UserPlus className="h-3.5 w-3.5" />,
      action: (e?: React.MouseEvent) => {
        actionBtnRef.current = e?.currentTarget as HTMLButtonElement;
        setActionBtnRect(actionBtnRef.current?.getBoundingClientRect() || null);
        setActionPopover({ type: 'assign', ticket: t });
      },
    },
    {
      id: 'priority', label: 'Change Priority', icon: <Flag className="h-3.5 w-3.5" />,
      action: (e?: React.MouseEvent) => {
        actionBtnRef.current = e?.currentTarget as HTMLButtonElement;
        setActionBtnRect(actionBtnRef.current?.getBoundingClientRect() || null);
        setActionPopover({ type: 'priority', ticket: t });
      },
    },
    {
      id: 'status', label: 'Change Status', icon: <ArrowUpDown className="h-3.5 w-3.5" />,
      action: (e?: React.MouseEvent) => {
        actionBtnRef.current = e?.currentTarget as HTMLButtonElement;
        setActionBtnRect(actionBtnRef.current?.getBoundingClientRect() || null);
        setActionPopover({ type: 'status', ticket: t });
      },
    },
    { id: 'archive', label: t.archived ? 'Restore' : 'Archive', icon: <Archive className="h-3.5 w-3.5" />, action: () => handleArchive(t.ticketId, !t.archived) },
  ];

  if (loading && tickets.length === 0) {
    return (
      <div className="animate-pulse">
        {/* Skeleton header */}
        <div className="flex items-center justify-between mb-4">
          <div className="h-7 bg-slate-200 rounded-lg w-48" />
          <div className="flex items-center gap-2">
            <div className="h-8 bg-slate-200 rounded-lg w-28" />
            <div className="h-8 bg-slate-200 rounded-lg w-20" />
          </div>
        </div>
        {/* Skeleton filter bar */}
        <div className="bg-white border border-slate-200 rounded-xl p-3 mb-4 flex items-center gap-3">
          <div className="h-8 bg-slate-200 rounded-lg flex-1" />
          <div className="h-8 bg-slate-200 rounded-lg w-24" />
          <div className="h-8 bg-slate-200 rounded-lg w-24" />
          <div className="h-8 bg-slate-200 rounded-lg w-10" />
        </div>
        {/* Skeleton table */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="p-4 space-y-3">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="flex items-center gap-3">
                <div className="h-3 w-3 rounded-full bg-slate-200" />
                <div className="h-4 bg-slate-200 rounded flex-1" />
                <div className="h-4 bg-slate-200 rounded w-16" />
                <div className="h-4 bg-slate-200 rounded w-16" />
                <div className="h-4 bg-slate-200 rounded w-20" />
                <div className="h-4 bg-slate-200 rounded w-12" />
                <div className="h-4 bg-slate-200 rounded w-12" />
                <div className="h-4 bg-slate-200 rounded w-10" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Compact search + filter controls — rendered in the top bar (list view)
  // and inside the ticket-list column header (workspace view) so they never
  // push the conversation down the screen.
  const renderFilterControls = () => (
    <>
      {/* Search */}
      <div className="relative flex-1 min-w-[110px]">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-slate-400" />
        <input type="text" ref={searchInputRef} value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search..." className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-6 pr-2 py-1.5 text-[11px] outline-none focus:border-blue-400 focus:bg-white min-h-[30px]" />
      </div>
      {/* Status */}
      <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value as TicketStatus | '')}
        className="bg-white border border-slate-200 rounded-lg px-1.5 py-1.5 text-[11px] font-bold outline-none focus:border-blue-400 cursor-pointer min-h-[30px] max-w-[110px]">
        <option value="">Status</option>
        {(Object.entries(STATUS_LABELS) as [TicketStatus, string][]).map(([v, l]) => (<option key={v} value={v}>{l}</option>))}
      </select>
      {/* Priority */}
      <select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value as TicketPriority | '')}
        className="bg-white border border-slate-200 rounded-lg px-1.5 py-1.5 text-[11px] font-bold outline-none focus:border-blue-400 cursor-pointer min-h-[30px] max-w-[100px]">
        <option value="">Priority</option>
        {(['urgent', 'high', 'medium', 'low'] as TicketPriority[]).map(p => (<option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>))}
      </select>
      {/* More filters */}
      <div className="relative">
        <button
          ref={moreFiltersBtnRef}
          onClick={() => {
            setShowFiltersPopover(p => !p);
            const r = moreFiltersBtnRef.current?.getBoundingClientRect();
            if (r) setFiltersPos({ top: r.bottom + 4, left: Math.max(8, Math.min(r.left, window.innerWidth - 230)) });
          }}
          className={`px-2 py-1.5 text-[11px] font-bold rounded-lg border transition-all cursor-pointer flex items-center gap-1 min-h-[30px] ${
            assignedFilter !== 'all' || filterLastReply !== 'all' || sortBy !== 'newest' || showArchived || !!programFilter || !!createdAfter || !!createdBefore || activityRange !== 'all'
              ? 'bg-blue-50 border-blue-200 text-brand' : 'text-slate-500 border-slate-200 hover:border-slate-400'
          }`} type="button">
          <Filter className="h-3 w-3" />
          {assignedFilter !== 'all' || filterLastReply !== 'all' || sortBy !== 'newest' || showArchived || !!programFilter || !!createdAfter || !!createdBefore || activityRange !== 'all' ? (
            <span className="ml-0.5 btn-primary text-white text-[11px] rounded-full w-4 h-4 flex items-center justify-center">
              {[assignedFilter !== 'all' ? 1 : 0, filterLastReply !== 'all' ? 1 : 0, sortBy !== 'newest' ? 1 : 0, showArchived ? 1 : 0, programFilter ? 1 : 0, createdAfter ? 1 : 0, createdBefore ? 1 : 0, activityRange !== 'all' ? 1 : 0].filter(Boolean).length}
            </span>
          ) : null}
        </button>
        {showFiltersPopover && (
          <div ref={filtersPopoverRef}
            className="fixed z-40 bg-white border border-slate-200 rounded-xl shadow-2xl p-3 w-[220px] space-y-2.5 max-h-[70vh] overflow-y-auto overscroll-contain"
            style={filtersPos ? { top: filtersPos.top, left: filtersPos.left } : undefined}
            onClick={e => e.stopPropagation()}>
            <div><label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Assignee</label>
              <select value={assignedFilter} onChange={(e) => setAssignedFilter(e.target.value as any)} className="w-full bg-white border border-slate-200 rounded-lg px-2 py-2 text-xs font-bold outline-none focus:border-blue-400 cursor-pointer min-h-[36px]">
                <option value="all">All tickets</option><option value="unassigned">Unassigned</option><option value="me">Assigned to me</option>
              </select></div>
            <div><label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Last Reply</label>
              <select value={filterLastReply} onChange={(e) => setFilterLastReply(e.target.value as any)} className="w-full bg-white border border-slate-200 rounded-lg px-2 py-2 text-xs font-bold outline-none focus:border-blue-400 cursor-pointer min-h-[36px]">
                <option value="all">All replies</option><option value="applicant">Applicant replied last</option>
              </select></div>
            <div><label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Sort By</label>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value as any)} className="w-full bg-white border border-slate-200 rounded-lg px-2 py-2 text-xs font-bold outline-none focus:border-blue-400 cursor-pointer min-h-[36px]">
                <option value="newest">Newest first</option><option value="oldest">Oldest first</option><option value="priority">Priority (high → low)</option>
              </select></div>
            <div><label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Program</label>
              <select value={programFilter} onChange={(e) => setProgramFilter(e.target.value)} className="w-full bg-white border border-slate-200 rounded-lg px-2 py-2 text-xs font-bold outline-none focus:border-blue-400 cursor-pointer min-h-[36px]">
                <option value="">All programs</option>
                {programOptions.map(p => (<option key={p} value={p}>{p}</option>))}
              </select></div>
            <div><label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Last Activity</label>
              <select value={activityRange} onChange={(e) => setActivityRange(e.target.value as any)} className="w-full bg-white border border-slate-200 rounded-lg px-2 py-2 text-xs font-bold outline-none focus:border-blue-400 cursor-pointer min-h-[36px]">
                <option value="all">Any time</option><option value="24h">Last 24 hours</option><option value="7d">Last 7 days</option><option value="30d">Last 30 days</option>
              </select></div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Created After</label>
                <input type="date" value={createdAfter} onChange={(e) => setCreatedAfter(e.target.value)} className="w-full bg-white border border-slate-200 rounded-lg px-2 py-1.5 text-[11px] font-bold outline-none focus:border-blue-400 min-h-[36px]" /></div>
              <div><label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Created Before</label>
                <input type="date" value={createdBefore} onChange={(e) => setCreatedBefore(e.target.value)} className="w-full bg-white border border-slate-200 rounded-lg px-2 py-1.5 text-[11px] font-bold outline-none focus:border-blue-400 min-h-[36px]" /></div>
            </div>
            <div className="border-t border-slate-100 pt-2 flex items-center justify-between">
              <span className="text-[10px] font-bold text-slate-400">Show archived</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" checked={showArchived} onChange={() => setShowArchived(!showArchived)} className="sr-only peer" />
                <div className="w-7 h-4 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-brand-primary after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-3 after:w-3 after:transition-all" />
              </label>
            </div>
          </div>
        )}
      </div>
      {/* Active filter clear */}
      {(assignedFilter !== 'all' || filterLastReply !== 'all' || sortBy !== 'newest' || showArchived || !!programFilter || !!createdAfter || !!createdBefore || activityRange !== 'all') && (
        <button onClick={clearAllFilters}
          className="px-2 py-1 text-[11px] sm:text-[10px] font-bold text-rose-500 hover:bg-rose-50 rounded-lg transition-all cursor-pointer whitespace-nowrap" type="button">
          Clear ×
        </button>
      )}
    </>
  );

  return (
    <div className="space-y-4 min-w-0 overflow-x-hidden">
      {/* Toast container */}
      <ToastContainer onOpenTicket={(tid) => handleNotifOpenTicket(tid)} />

      {/* Header — pinned: the three-column split view is a fixed height, so
          without this the header slid up behind it and only a sliver of the
          dark bar (with its "N total conversations" line) was visible at the
          top edge, looking like ghosted UI bleeding through. */}
      <div className="sticky top-0 z-30 rounded-2xl border border-slate-200 bg-gradient-to-r from-slate-900 via-slate-800 to-blue-900 p-4 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-blue-100 ring-1 ring-white/10">
              <TicketCheck className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-base font-extrabold text-white">Support Tickets</h1>
                {openCount > 0 && (
                  <span className="rounded-full border border-rose-300/30 bg-rose-500/15 px-2 py-0.5 text-[10px] font-bold text-rose-100">
                    {openCount} open
                  </span>
                )}
              </div>
              <p className="text-[11px] text-slate-300">{tickets.length} {tickets.length === 1 ? 'total conversation' : 'total conversations'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 self-start lg:self-auto">
            <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 p-1">
              <TaskNotificationCenter />

              <div className="relative">
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className={`relative flex h-9 w-9 items-center justify-center rounded-lg transition-all cursor-pointer ${
                    unreadCount > 0 ? 'bg-brand-primary/20 text-blue-100 hover:bg-brand-primary/30' : 'text-slate-300 hover:bg-white/5 hover:text-white'
                  }`}
                  title="Notifications"
                  aria-label="Notifications"
                  type="button"
                >
                  {unreadCount > 0 ? <BellRing className="h-4 w-4" /> : <Bell className="h-4 w-4" />}
                  {unreadCount > 0 && (
                    <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-rose-500 text-[11px] font-bold text-white">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
              </div>

              <div className="relative">
                <button
                  onClick={() => setShowSoundPrefs(!showSoundPrefs)}
                  className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-300 transition-all cursor-pointer hover:bg-white/5 hover:text-white"
                  title="Settings & sound preferences"
                  aria-label="Settings and preferences"
                  type="button"
                >
                  <Settings className="h-4 w-4" />
                </button>
                {showSoundPrefs && (
                  <div className="absolute right-0 top-full mt-2 z-50 bg-white border border-slate-200 rounded-xl shadow-xl p-2 min-w-[200px] space-y-1">
                    <button
                      onClick={() => { setShowShortcuts(true); setShowSoundPrefs(false); }}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 rounded-lg cursor-pointer"
                      type="button"
                    >
                      <Keyboard className="h-4 w-4 text-slate-400" /> Keyboard shortcuts (?)
                    </button>
                    <div className="border-t border-slate-100 pt-1">
                      <SoundPrefsPanel onClose={() => setShowSoundPrefs(false)} />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              {canEdit && (
                <button
                  onClick={() => setShowCreate(true)}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-white px-3 py-2 text-xs font-bold text-slate-900 shadow-sm transition-all hover:bg-slate-100 cursor-pointer"
                >
                  <Plus className="h-3.5 w-3.5" /> New Ticket
                </button>
              )}
              <button
                onClick={() => fetchTickets(showArchived)}
                disabled={refreshing}
                className="inline-flex items-center gap-1.5 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs font-bold text-white transition-all hover:bg-white/10 disabled:opacity-50 cursor-pointer"
              >
                {refreshing ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="h-3.5 w-3.5" />} Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Workspace: sidebar + main column ── */}
      <div className="flex min-w-0 gap-4 items-start overflow-x-hidden">
        {/* Left sidebar — desktop only */}
        <TicketSidebar
          section={section}
          onSectionChange={handleSectionChange}
          counts={sectionCounts}
          unreadCounts={unreadSectionCounts}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(c => !c)}
        />

        <div className="flex-1 min-w-0 space-y-4 overflow-x-hidden">
          {/* Mobile section strip */}
          <TicketSectionStrip
            section={section}
            onSectionChange={handleSectionChange}
            counts={sectionCounts}
            unreadCounts={unreadSectionCounts}
          />

          {/* Active filter chips */}
          {hasActiveFilters && (
            <div className="flex items-center gap-1.5 flex-wrap">
              {section !== 'all' && (
                <FilterChip label={`View: ${TICKET_SECTION_LABELS[section]}`} onClear={() => setSection('all')} />
              )}
              {filterStatus && (
                <FilterChip label={`Status: ${STATUS_LABELS[filterStatus]}`} onClear={() => setFilterStatus('')} />
              )}
              {filterPriority && (
                <FilterChip label={`Priority: ${filterPriority}`} onClear={() => setFilterPriority('')} />
              )}
              {assignedFilter === 'unassigned' && (
                <FilterChip label="Unassigned" onClear={() => setAssignedFilter('all')} />
              )}
              {assignedFilter === 'me' && (
                <FilterChip label="Assigned to me" onClear={() => setAssignedFilter('all')} />
              )}
              {filterLastReply === 'applicant' && (
                <FilterChip label="Applicant replied last" onClear={() => setFilterLastReply('all')} />
              )}
              {programFilter && (
                <FilterChip label={`Program: ${programFilter}`} onClear={() => setProgramFilter('')} />
              )}
              {activityRange !== 'all' && (
                <FilterChip label={`Activity: ${activityRange === '24h' ? 'last 24h' : activityRange === '7d' ? 'last 7d' : 'last 30d'}`} onClear={() => setActivityRange('all')} />
              )}
              {createdAfter && (
                <FilterChip label={`Created after ${createdAfter}`} onClear={() => setCreatedAfter('')} />
              )}
              {createdBefore && (
                <FilterChip label={`Created before ${createdBefore}`} onClear={() => setCreatedBefore('')} />
              )}
              {showArchived && section === 'all' && (
                <FilterChip label="Showing archived" onClear={() => setShowArchived(false)} />
              )}
              <button
                onClick={clearAllFilters}
                className="px-2 py-1 text-[10px] font-bold text-rose-500 hover:bg-rose-50 rounded-lg transition-all cursor-pointer"
                type="button"
              >
                Clear all ×
              </button>
            </div>
          )}

      {/* Filters — compact row, only in list view. In the workspace they live in the list-column header. */}
      {!selectedTicket && (
        <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-2.5 shadow-sm">
          <div className="flex items-center gap-1.5 flex-wrap">
            {renderFilterControls()}
          </div>
        </div>
      )}

      {/* ── Ticket List: desktop table + mobile card list (replaced by the workspace when a ticket is open) ── */}
      {!selectedTicket && filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl py-12 sm:py-16 text-center">
          <div className="flex flex-col items-center gap-2 sm:gap-3">
            {tickets.length === 0 ? (
              <>
                <TicketCheck className="h-10 w-10 sm:h-12 sm:w-12 text-slate-300" />
                <p className="text-sm sm:text-base text-slate-400 font-semibold">No support tickets yet</p>
                <button onClick={() => setShowCreate(true)} type="button"
                  className="mt-1 px-4 py-2 text-xs font-bold text-white btn-primary  rounded-xl transition-all cursor-pointer">
                  Create first ticket
                </button>
              </>
            ) : (
              <>
                <Search className="h-10 w-10 sm:h-12 sm:w-12 text-slate-300" />
                <p className="text-sm sm:text-base text-slate-400 font-semibold">
                  {section !== 'all' ? `No ${TICKET_SECTION_LABELS[section].toLowerCase()} tickets` : 'No tickets match your current filters'}
                </p>
                <button onClick={() => { if (section !== 'all') setSection('all'); else clearAllFilters(); }} type="button"
                  className="mt-1 px-4 py-2 text-xs font-bold text-brand bg-blue-50 hover:bg-blue-100 rounded-xl transition-all cursor-pointer">
                  {section !== 'all' ? 'View all tickets' : 'Clear filters'}
                </button>
              </>
            )}
          </div>
        </div>
      ) : (
        <>
          {/* ── Desktop table (md+) — replaced by the split workspace when a ticket is open ── */}
          <div className={`${selectedTicket ? 'hidden' : 'hidden md:block'} overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm`}>
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="bg-slate-100 text-[10px] font-bold uppercase tracking-[0.08em] text-slate-500">
                  <th className="w-8 px-4 py-3">
                    <input type="checkbox" checked={selectedIds.length === filtered.length && filtered.length > 0}
                      onChange={() => setSelectedIds(selectedIds.length === filtered.length ? [] : filtered.map(t => t.ticketId))}
                      className="rounded border-slate-300 text-brand focus:ring-brand-primary cursor-pointer" />
                  </th>
                  <th className="px-4 py-3">Ticket ID</th>
                  <th className="px-4 py-3">Applicant</th>
                  <th className="px-4 py-3">Issue</th>
                  <th className="px-4 py-3">Last Activity</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Priority</th>
                  <th className="px-4 py-3">Assigned To</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {pagedTickets.map(t => (
                  <tr key={t.ticketId} onClick={() => openTicket(t)}
                    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && openTicket(t)}
                    tabIndex={0} role="button" aria-label={`Open ticket: ${t.title}`}
                    className={`cursor-pointer transition-all outline-none focus:ring-2 focus:ring-brand-primary focus:ring-inset ${t.unread ? 'bg-blue-50/80' : 'hover:bg-slate-50'} ${t.unread ? 'shadow-[inset_0_0_0_1px_rgba(37,99,235,0.04)]' : ''}`}>
                    <td className="px-4 py-3 w-8">
                      <div className="flex items-center gap-1">
                        <input type="checkbox" checked={selectedIds.includes(t.ticketId)}
                          onChange={(e) => { e.stopPropagation(); setSelectedIds(prev => prev.includes(t.ticketId) ? prev.filter(id => id !== t.ticketId) : [...prev, t.ticketId]); }}
                          onClick={(e) => e.stopPropagation()}
                          className="rounded border-slate-300 text-brand focus:ring-brand-primary cursor-pointer shrink-0" />
                        {t.unread && <div className="w-2 h-2 rounded-full bg-brand-primary shrink-0" />}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className={`font-mono font-bold ${t.unread ? 'text-brand' : 'text-slate-500'}`}>#{t.ticketId.slice(0, 8)}</span>
                        {t.unread && <span className="rounded-full bg-blue-100 px-1.5 py-0.5 text-[11px] font-extrabold text-brand">New</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <ParticipantAvatar
                          name={t.applicantName}
                          photoUrl={ticketPhoto(t)}
                          size="sm"
                        />
                        <div className="min-w-0">
                          <div className={`${t.unread ? 'font-extrabold' : 'font-bold'} truncate text-slate-800`}>{t.applicantName || '—'}</div>
                          <div className="truncate font-mono text-[11px] text-slate-500">{t.applicantEmail}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 max-w-[220px]">
                      <div className={`flex items-center gap-1.5 ${t.unread ? 'font-extrabold text-slate-900' : 'font-bold text-slate-700'} leading-tight`}>
                        {t.viewerPinned && <Pin className="h-3 w-3 shrink-0 text-blue-500 fill-blue-500" aria-label="Pinned by you" />}
                        {(() => {
                          const src = t.source || (t.employerId ? 'employer' : t.referrerId ? 'referrer' : t.sponsorId ? 'sponsor' : t.createdByRole);
                          if (src === 'employer') return <span className="shrink-0 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-extrabold">Employer Ticket</span>;
                          if (src === 'referrer') return <span className="shrink-0 px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 text-[10px] font-extrabold">Referrer Ticket</span>;
                          if (src === 'sponsor') return <span className="shrink-0 px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 text-[10px] font-extrabold">Sponsor Ticket</span>;
                          return null;
                        })()}
                        <span className="truncate">{t.title}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-[11px] text-slate-500"><Clock className="h-3 w-3" /><span>{formatLastActivity(t.lastCommentAt || t.updatedAt || t.createdAt)}</span></div>
                      <div className="mt-1"><SlaBadge ticket={t} dense /></div>
                    </td>
                    <td className="px-4 py-3"><StatusPill status={t.status} /></td>
                    <td className="px-4 py-3"><PriorityPill priority={t.priority} /></td>
                    <td className="px-4 py-3">
                      {(() => {
                        const n = Array.isArray(t.assignees) && t.assignees.length > 0
                          ? t.assignees.length
                          : (t.assignedTo ? 1 : 0);
                        if (n === 0) return <span className="text-[10px] font-semibold text-slate-400">—</span>;
                        return (
                          <span className="flex items-center gap-1 text-[10px] font-semibold text-slate-600">
                            <span className="truncate">{t.assignedToName || t.assignees?.[0]?.name || 'Assigned'}</span>
                            {n > 1 && (
                              <span
                                className="shrink-0 rounded-full bg-slate-100 px-1.5 py-0.5 text-[11px] font-bold text-slate-500"
                                title={`${n} assignees: ${(t.assignees || []).map(a => a.name).join(', ')}`}
                              >
                                +{n - 1}
                              </span>
                            )}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        <div>
                          <button aria-label="Quick actions" title="Quick actions" onClick={(e) => { actionBtnRef.current = e.currentTarget as HTMLButtonElement; setActionBtnRect(actionBtnRef.current?.getBoundingClientRect() || null); setActiveQuickAction(activeQuickAction === t.ticketId ? null : t.ticketId); }}
                            className="rounded-lg bg-slate-100 p-1.5 text-slate-500 transition-all hover:bg-blue-50 hover:text-brand cursor-pointer" type="button" data-quick-action={t.ticketId}>
                            <MoreHorizontal className="h-3.5 w-3.5" />
                          </button>
                          {activeQuickAction === t.ticketId && ReactDOM.createPortal(
                            <div className="fixed z-[80] bg-white border border-slate-200 rounded-xl shadow-2xl py-1 min-w-[180px]"
                              style={{ top: actionBtnRect ? actionBtnRect.bottom + 4 : 'auto', right: actionBtnRect ? window.innerWidth - actionBtnRect.right : 'auto' }}
                              onClick={e => e.stopPropagation()}>
                              {quickActions(t).map(action => (
                                <button key={action.id} onClick={(e) => { action.action(e); setActiveQuickAction(null); }}
                                  className="w-full flex items-center gap-2.5 px-3 py-2 text-[11px] font-semibold text-slate-700 hover:bg-slate-50 transition-all cursor-pointer min-h-[36px]" type="button">
                                  <span className="text-slate-400 shrink-0">{action.icon}</span>{action.label}
                                </button>
                              ))}
                            </div>, document.body
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── Mobile card list (<md) — replaced by the split workspace when a ticket is open ── */}
          <div className={`${selectedTicket ? 'hidden' : ''} md:hidden space-y-3`}>
            {pagedTickets.map(t => (
              <div key={t.ticketId} onClick={() => openTicket(t)}
                className={`bg-white border rounded-xl overflow-hidden transition-all active:scale-[0.98] ${t.unread ? 'border-l-[3px] border-l-blue-500 border-slate-200' : 'border-slate-200'}`}>
                <div className="p-3.5 pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <input type="checkbox" checked={selectedIds.includes(t.ticketId)}
                          onChange={(e) => { e.stopPropagation(); setSelectedIds(prev => prev.includes(t.ticketId) ? prev.filter(id => id !== t.ticketId) : [...prev, t.ticketId]); }}
                          onClick={(e) => e.stopPropagation()}
                          className="rounded border-slate-300 text-brand focus:ring-brand-primary cursor-pointer shrink-0 -ml-0.5 mr-0.5" />
                        {t.unread && <div className="w-2 h-2 rounded-full bg-brand-primary shrink-0" />}
                        <span className="text-[10px] font-mono font-bold text-slate-400">#{t.ticketId.slice(0, 8)}</span>
                        {t.unread && <span className="px-1.5 py-0.5 bg-blue-100 text-brand text-[11px] font-extrabold rounded-full">New</span>}
                      </div>
                      <div className="flex items-center gap-1.5 mt-1">
                        <ParticipantAvatar
                          name={t.applicantName}
                          photoUrl={ticketPhoto(t)}
                          size="xs"
                        />
                        <span className="font-semibold text-slate-600 truncate max-w-[110px] text-xs">{t.applicantName || '—'}</span>
                        <span className="text-slate-300">·</span>
                        <span className="flex items-center gap-1 text-slate-400 text-xs">
                          <Clock className="h-3 w-3" />
                          {formatLastActivity(t.lastCommentAt || t.updatedAt || t.createdAt)}
                        </span>
                        {t.attachments && t.attachments.length > 0 && <Paperclip className="h-3 w-3 text-slate-300 shrink-0" />}
                      </div>
                      <h3 className={`text-sm leading-snug mt-1 ${t.unread ? 'font-extrabold text-slate-900' : 'font-bold text-slate-800'} line-clamp-2`}>
                        {t.viewerPinned && <Pin className="inline h-3.5 w-3.5 -mt-0.5 mr-1 text-blue-500 fill-blue-500" aria-label="Pinned by you" />}
                        {t.title}
                      </h3>
                      <div className="mt-1.5"><SlaBadge ticket={t} dense /></div>
                      {t.assignedToName && (
                        <div className="flex items-center gap-1 text-[11px] text-slate-500 mt-0.5">
                          <User className="h-2.5 w-2.5" />
                          <span className="truncate">Assigned: {t.assignedToName}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      {(() => {
                        const src = t.source || (t.employerId ? 'employer' : t.referrerId ? 'referrer' : t.sponsorId ? 'sponsor' : t.createdByRole);
                        if (src === 'employer') return <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-extrabold">Employer Ticket</span>;
                        if (src === 'referrer') return <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 text-[10px] font-extrabold">Referrer Ticket</span>;
                        if (src === 'sponsor') return <span className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 text-[10px] font-extrabold">Sponsor Ticket</span>;
                        return null;
                      })()}
                      <StatusPill status={t.status} />
                      <PriorityPill priority={t.priority} />
                    </div>
                  </div>
                </div>
                {t.lastCommentBody && (
                  <div className="px-3.5 py-2 bg-slate-50/80 border-t border-slate-100 flex items-center gap-1.5">
                    {t.lastCommentBy === 'applicant' && <Mail className="h-3 w-3 text-blue-400 shrink-0" />}
                    <span className="text-[11px] text-slate-500 truncate flex-1">
                      {t.lastCommentAuthor && <span className="font-semibold text-slate-500">{t.lastCommentAuthor}: </span>}
                      {t.lastCommentBody}
                    </span>
                    <div className="flex items-center gap-1 shrink-0">
                      <button aria-label={t.archived ? 'Unarchive ticket' : 'Archive ticket'} title={t.archived ? 'Unarchive ticket' : 'Archive ticket'} onClick={(e) => { e.stopPropagation(); handleArchive(t.ticketId, !t.archived); }} disabled={archivingId === t.ticketId}
                        className="p-1.5 text-slate-400 hover:text-amber-600 rounded-lg transition-all cursor-pointer disabled:opacity-40" type="button">
                        {archivingId === t.ticketId ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className={`h-3.5 w-3.5 ${t.archived ? 'text-amber-500' : ''}`} />}
                      </button>
                      <button aria-label="Quick actions" title="Quick actions" onClick={(e) => { e.stopPropagation(); actionBtnRef.current = e.currentTarget as HTMLButtonElement; setActionBtnRect(actionBtnRef.current?.getBoundingClientRect() || null); setActiveQuickAction(activeQuickAction === t.ticketId ? null : t.ticketId); }}
                        className="p-1.5 text-slate-400 hover:text-brand rounded-lg transition-all cursor-pointer" type="button" data-quick-action={t.ticketId}>
                        <MoreHorizontal className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          {hasMoreTickets && (
            <button
              onClick={loadMoreTickets}
              className="w-full mt-3 py-2.5 text-xs font-bold text-brand bg-blue-50 hover:bg-blue-100 rounded-xl transition-all cursor-pointer"
              type="button"
            >
              Load more ({filtered.length - pagedTickets.length} remaining)
            </button>
          )}
        </>
      )}


      {/* ── Bulk Action Toolbar (floating) ── */}
      {selectedIds.length > 0 && ReactDOM.createPortal(
        <div className="fixed bottom-0 left-0 right-0 z-[8000] p-3 sm:p-4 pointer-events-none">
          <div className="max-w-4xl mx-auto bg-white border border-slate-200 rounded-xl shadow-2xl p-2.5 sm:p-3 flex items-center justify-between gap-2 pointer-events-auto animate-slide-up">
            <div className="flex items-center gap-2 sm:gap-3">
              <span className="text-[11px] sm:text-xs font-bold text-slate-600 whitespace-nowrap">
                {selectedIds.length} selected
              </span>
              <button onClick={() => setSelectedIds([])}
                className="text-[11px] sm:text-xs text-slate-500 hover:text-slate-600 font-semibold transition-all cursor-pointer whitespace-nowrap" type="button">
                Clear
              </button>
              <div className="w-px h-5 bg-slate-200 hidden sm:block" />
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap justify-end">
              {/* Bulk Status */}
              <select onChange={(e) => { if (e.target.value) handleBulkAction('status', e.target.value); e.target.value = ''; }}
                className="bg-white border border-slate-200 rounded-lg px-2 py-1.5 sm:py-2 text-[10px] sm:text-xs font-bold outline-none focus:border-blue-400 cursor-pointer min-h-[32px] sm:min-h-[36px]" disabled={!!bulkAction}>
                <option value="">Status</option>
                {(Object.entries(STATUS_LABELS) as [string, string][]).map(([v, l]) => (<option key={v} value={v}>{l}</option>))}
              </select>
              {/* Bulk Priority */}
              <select onChange={(e) => { if (e.target.value) handleBulkAction('priority', e.target.value); e.target.value = ''; }}
                className="bg-white border border-slate-200 rounded-lg px-2 py-1.5 sm:py-2 text-[10px] sm:text-xs font-bold outline-none focus:border-blue-400 cursor-pointer min-h-[32px] sm:min-h-[36px]" disabled={!!bulkAction}>
                <option value="">Priority</option>
                {(['urgent', 'high', 'medium', 'low'] as string[]).map(p => (<option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>))}
              </select>
              {/* Bulk Archive */}
              <button onClick={() => handleBulkAction('archive', true)} disabled={!!bulkAction}
                className="px-2.5 py-1.5 sm:py-2 text-[10px] sm:text-xs font-bold text-amber-600 hover:bg-amber-50 border border-slate-200 rounded-lg transition-all cursor-pointer disabled:opacity-40 min-h-[32px] sm:min-h-[36px] flex items-center gap-1" type="button">
                <Archive className="h-3 w-3" />
                <span className="hidden sm:inline">Archive</span>
              </button>
              {bulkAction && <Loader className="h-3.5 w-3.5 animate-spin text-blue-500 ml-1" />}
            </div>
          </div>
        </div>,
        document.body
      )}
      {/* Notification Center */}{/* Notification Center */}
      {showNotifications && (
        <NotificationCenter
          onClose={() => setShowNotifications(false)}
          notifications={notifications}
          onMarkRead={handleMarkNotifRead}
          onMarkAllRead={handleMarkAllNotifRead}
          onDelete={handleDeleteNotif}
          onOpenTicket={handleNotifOpenTicket}
        />
      )}

      {/* Action Popover (portal-based) */}
      {actionPopover && ReactDOM.createPortal(
        <ActionPopover
          type={actionPopover.type}
          ticket={actionPopover.ticket}
          anchorRect={actionBtnRect}
          allAdmins={allAdmins || []}
          onUpdate={async (ticketId, updates) => {
            await handleUpdate(ticketId, updates);
            // Optimistic: update local state immediately
            setTickets(prev => prev.map(t =>
              t.ticketId === ticketId ? { ...t, ...updates } : t,
            ));
            setActionPopover(null);
          }}
          onClose={() => setActionPopover(null)}
        />,
        document.body
      )}

      {/* Split workspace: ticket list + conversation + details (no popup) */}
      {selectedTicket ? (
        <div
          ref={attachWorkspace}
          className="ticket-workspace grid w-full max-w-full gap-4 items-start overflow-x-hidden min-w-0"
          style={{ gridTemplateColumns: workspaceColumns }}
        >
          {/* Left: condensed ticket list (md+) — collapsible */}
          {leftListCollapsed ? (
            <div className="hidden md:flex w-9 min-w-0 h-[calc(100vh-155px)]">
              <div className="w-full bg-white border border-slate-200 rounded-xl flex flex-col items-center py-2 gap-2.5 overflow-hidden">
                <button
                  onClick={() => setLeftListCollapsed(false)}
                  className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-brand transition-all cursor-pointer"
                  title="Show ticket list"
                  aria-label="Show ticket list"
                  type="button"
                >
                  <PanelLeftOpen className="h-4 w-4" />
                </button>
                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest [writing-mode:vertical-rl] rotate-180">
                  {filtered.length} tickets
                </span>
              </div>
            </div>
          ) : (
          <div className="hidden md:block w-full max-w-[320px] min-w-0 overflow-x-hidden h-[calc(100vh-155px)]">
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden flex flex-col h-full min-w-0">
              <div className="px-3 py-2 border-b border-slate-200 bg-slate-50/80 shrink-0 min-w-0">
                <div className="flex items-center justify-between gap-2 min-w-0">
                  <span className="text-xs font-bold text-slate-700 truncate">{TICKET_SECTION_LABELS[section]}</span>
                  <div className="flex items-center gap-1 shrink-0">
                    <span className="text-[10px] font-bold text-slate-400">{filtered.length}</span>
                    <button
                      onClick={() => setLeftListCollapsed(true)}
                      className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-brand transition-all cursor-pointer"
                      title="Collapse ticket list"
                      aria-label="Collapse ticket list"
                      type="button"
                    >
                      <PanelLeftClose className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
                {/* Search + filters live here in the workspace so the top bar stays out of the chat's way */}
                <div className="mt-2 flex items-center gap-1.5 flex-wrap min-w-0">
                  {renderFilterControls()}
                </div>
              </div>
              <div className="flex-1 overflow-y-auto overflow-x-hidden divide-y divide-slate-100 min-w-0">
                {pagedTickets.map(t => {
                  const isSelected = selectedTicket?.ticketId === t.ticketId;
                  return (
                    <div key={t.ticketId} onClick={() => openTicket(t)}
                      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && openTicket(t)}
                      tabIndex={0} role="button"
                      aria-label={`Open ticket: ${t.title}`}
                      className={`msg-ticket-row group outline-none focus:ring-2 focus:ring-inset focus:ring-brand-primary ${
                        isSelected ? 'msg-ticket-row--active' : ''
                      }`}>
                      <div className="flex items-start gap-2">
                        {/* Avatar */}
                        <div className="relative shrink-0">
                          <ParticipantAvatar
                            name={t.applicantName}
                            photoUrl={ticketPhoto(t)}
                            size="md"
                          />
                          {t.unread && <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-brand-primary rounded-full border-2 border-white" />}
                        </div>
                        {/* Content */}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <span className={`text-[11px] truncate ${t.unread ? 'font-extrabold text-slate-900' : 'font-bold text-slate-700'}`}>
                              {t.applicantName || '—'}
                            </span>
                            <span className="flex items-center gap-1 text-[11px] text-slate-500 shrink-0">
                              {t.attachments && t.attachments.length > 0 && <Paperclip className="h-3 w-3" />}
                              <Clock className="h-3 w-3" />
                              <span>{formatLastActivity(t.lastCommentAt || t.updatedAt || t.createdAt)}</span>
                            </span>
                          </div>
                          <p className={`text-[11px] leading-snug truncate mt-0.5 ${t.unread ? 'font-bold text-slate-800' : 'text-slate-600'}`}>
                            {t.viewerPinned && <Pin className="inline h-3 w-3 -mt-0.5 mr-1 text-blue-500 fill-blue-500" aria-label="Pinned by you" />}
                            {t.title}
                          </p>
                          {t.lastCommentBody && (
                            <p className="text-[11px] text-slate-500 truncate mt-0.5">
                              {t.lastCommentBy === 'applicant' && <Mail className="h-2.5 w-2.5 inline-block -mt-0.5 mr-0.5 text-blue-400" />}
                              {t.lastCommentAuthor && <span className="font-semibold text-slate-500">{t.lastCommentAuthor}: </span>}
                              {t.lastCommentBody}
                            </p>
                          )}
                          <div className="flex items-center gap-1.5 mt-1.5 min-w-0">
                            <SlaBadge ticket={t} dense urgentOnly />
                            <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded-full border shrink-0 ${STATUS_COLORS[t.status]}`}>{STATUS_LABELS[t.status]}</span>
                            <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${t.priority === 'urgent' ? 'bg-rose-50 text-rose-700' : t.priority === 'high' ? 'bg-orange-50 text-orange-700' : t.priority === 'medium' ? 'bg-brand-active' : 'bg-slate-100 text-slate-500'}`}>
                              {t.priority}
                            </span>
                            {t.assignedToName && (
                              <span className="text-[11px] text-slate-500 truncate ml-auto flex items-center gap-1">
                                <User className="h-2.5 w-2.5 shrink-0" />
                                <span className="truncate">{t.assignedToName}</span>
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {hasMoreTickets && (
                  <button
                    onClick={loadMoreTickets}
                    className="w-full py-2 text-[10px] font-bold text-brand hover:bg-blue-50 transition-colors cursor-pointer"
                    type="button"
                  >
                    Show more ({filtered.length - pagedTickets.length} remaining)
                  </button>
                )}
              </div>
            </div>
          </div>
          )}
          {/* Middle: embedded chat — Chat Phase 3 converged thread
              (AdminTicketThread → the shared chat engine, admin
              participant). The engine header provides the title, status /
              priority tags and the mobile back control, so the old
              page-level mobile drill-in bar is gone. The host's openTicket
              fetch still feeds the context rail (TicketSplitView) and
              still stamps the server read state. */}
          <div className="flex-1 min-w-0 bg-white border border-slate-200 rounded-xl overflow-hidden flex flex-col h-[calc(100vh-155px)]">
            <div className="flex-1 min-h-0 overflow-hidden flex flex-col min-w-0">
              <AdminTicketThread
                ticket={selectedTicket}
                onClose={() => setSelectedTicket(null)}
                onTicketChanged={mergeTicketUpdate}
                applicantPhotoUrl={ticketPhoto(selectedTicket)}
                onToggleDetails={() => { userToggledDetailsRef.current = true; setRightDetailsCollapsed(prev => !prev); }}
                detailsOpen={!rightDetailsCollapsed}
              />
            </div>
          </div>
          {/* Right: details panel — a grid track at ≥1100px, an overlay drawer
              below that so it never steals width from the composer. */}
          {detailsInGrid && (
            <div className="relative min-w-0 h-[calc(100vh-155px)]">
              {/* Drag-to-resize handle — sits in the grid gap, double-click resets. */}
              <div
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize details panel"
                onMouseDown={handleDetailsResizeStart}
                onDoubleClick={() => setDetailsWidth(360)}
                title="Drag to resize · double-click to reset"
                className="group absolute -left-2 top-0 z-10 hidden h-full w-4 cursor-col-resize items-center justify-center xl:flex"
              >
                <span className="h-10 w-1 rounded-full bg-slate-200 transition-colors group-hover:bg-brand/60" />
              </div>
              <TicketSplitView
                ticket={selectedTicket}
                comments={selectedTicketComments}
                onClose={() => setRightDetailsCollapsed(true)}
                onUpdate={handleUpdate}
                allAdmins={allAdmins}
                onViewApplicant={handleViewApplicant}
                applicantPhotoUrl={ticketPhoto(selectedTicket)}
              />
            </div>
          )}
          {detailsRail && (
            <div className="flex h-[calc(100vh-155px)] min-w-0 items-start justify-center rounded-xl border border-slate-200 bg-white pt-2">
              <button
                type="button"
                onClick={() => { userToggledDetailsRef.current = true; setRightDetailsCollapsed(false); }}
                className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-blue-50 hover:text-brand"
                aria-label="Open ticket details"
                title="Open ticket details"
              >
                <Settings className="h-4 w-4" />
              </button>
            </div>
          )}
          {detailsDrawer && (
            <div
              className="fixed inset-0 z-[8500] flex justify-end bg-slate-900/40 p-0 backdrop-blur-[2px] sm:p-3"
              onClick={() => setRightDetailsCollapsed(true)}
              role="dialog"
              aria-modal="true"
              aria-label="Ticket and applicant details"
            >
              {/* The drawer honours the panel width the admin dragged, capped to
                  the viewport on small screens so it can never sit off-canvas. */}
              <div
                className="h-full max-h-full min-w-0 overflow-hidden sm:rounded-2xl sm:shadow-2xl"
                style={{ width: `min(${Math.max(340, Math.min(detailsWidth, 520))}px, 94vw)` }}
                onClick={e => e.stopPropagation()}
              >
                <TicketSplitView
                  ticket={selectedTicket}
                  comments={selectedTicketComments}
                  onClose={() => setRightDetailsCollapsed(true)}
                  onUpdate={handleUpdate}
                  allAdmins={allAdmins}
                  onViewApplicant={handleViewApplicant}
                  applicantPhotoUrl={ticketPhoto(selectedTicket)}
                />
              </div>
            </div>
          )}
        </div>
      ) : null}
        </div>
      </div>

      {/* ── Keyboard shortcuts help ── */}
      {showShortcuts && (
        <Modal onClose={() => setShowShortcuts(false)} ariaLabel="Keyboard shortcuts">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-5" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-extrabold text-slate-900 flex items-center gap-2">
                <Keyboard className="h-4 w-4 text-brand" /> Keyboard Shortcuts
              </h3>
              <button onClick={() => setShowShortcuts(false)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-all cursor-pointer" type="button" aria-label="Close">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-2 text-xs">
              {[
                ['/', 'Focus ticket search'],
                ['j / k', 'Next / previous ticket'],
                ['↑ / ↓', 'Next / previous ticket (when one is open)'],
                ['n', 'Create a new ticket'],
                ['[ / ]', 'Next / previous ticket (legacy binding)'],
                ['r', 'Refresh tickets'],
                ['⌘/Ctrl + Enter', 'Send the reply you are typing'],
                ['Esc', 'Close the details panel or current popup'],
                ['?', 'Show this help'],
              ].map(([key, label]) => (
                <div key={key} className="flex items-center justify-between gap-3">
                  <span className="text-slate-500">{label}</span>
                  <kbd className="px-2 py-0.5 bg-slate-100 border border-slate-200 rounded-md font-mono text-[10px] font-bold text-slate-600 shrink-0">{key}</kbd>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-slate-500 mt-4 leading-relaxed">
              Shortcuts are disabled while you are typing in an input field.
            </p>
          </div>
        </Modal>
      )}

      {/* Create Ticket Modal (Admin) */}
      {showCreate && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4" style={{ zIndex: createZ }} onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 shrink-0">
              <div>
                <h3 className="text-sm font-extrabold text-slate-900">Create New Ticket</h3>
                <p className="text-caption text-slate-400 mt-0.5">Manually create a support ticket</p>
              </div>
              <button aria-label="Close" title="Close" onClick={() => setShowCreate(false)} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all cursor-pointer">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {/* Applicant Search — with lock-after-select pattern */}
              <div className="space-y-1.5">
                <label className="text-caption font-bold text-slate-500 uppercase">Applicant *</label>
                {newAppId ? (
                  <div className="flex items-center justify-between gap-2 p-2.5 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="min-w-0 flex-1">
                      <span className="text-xs font-bold text-blue-800 truncate block">
                        {applications.find((a: any) => a.applicationId === newAppId)?.personalDetails?.firstName || ''}{' '}
                        {applications.find((a: any) => a.applicationId === newAppId)?.personalDetails?.lastName || ''}
                      </span>
                      <span className="text-[10px] text-blue-500 truncate block">
                        {applications.find((a: any) => a.applicationId === newAppId)?.userEmail || ''}
                      </span>
                    </div>
                    <button
                      onClick={() => { setNewAppId(''); setAppSearch(''); }}
                      className="px-2 py-1 text-[10px] font-bold text-rose-500 hover:bg-rose-50 rounded-lg transition-all cursor-pointer shrink-0"
                      type="button"
                    >
                      Change ×
                    </button>
                  </div>
                ) : (
                  <>
                    <input
                      type="text"
                      ref={appSearchRef}
                      value={appSearch}
                      onChange={(e) => { setAppSearch(e.target.value); setAppSearchOpen(true); syncAppSearchRect(); }}
                      onFocus={() => { setAppSearchOpen(true); syncAppSearchRect(); }}
                      onBlur={() => window.setTimeout(() => setAppSearchOpen(false), 150)}
                      onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setAppSearchOpen(false); } }}
                      placeholder="Search by name, email, or application ID..."
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-blue-400"
                    />
                    {appSearchOpen && appSearchRect && ReactDOM.createPortal(
                      <div
                        className="fixed max-h-56 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-2xl divide-y divide-slate-100"
                        style={{ top: appSearchRect.bottom + 4, left: appSearchRect.left, width: appSearchRect.width }}
                        role="listbox"
                        aria-label="Matching applications"
                      >
                        {filteredApplications.length === 0 ? (
                          <p className="p-2 text-center text-caption text-slate-400">No matching applications.</p>
                        ) : filteredApplications.map((a: any) => (
                          <button
                            key={a.applicationId}
                            onClick={() => { setNewAppId(a.applicationId); setAppSearch(''); setAppSearchOpen(false); }}
                            className="w-full text-left px-3 py-2 text-xs transition-all hover:bg-brand-active cursor-pointer"
                            type="button"
                            role="option"
                            aria-selected={false}
                          >
                            <span className="font-bold text-slate-800">{a.personalDetails?.firstName || ''} {a.personalDetails?.lastName || ''}</span>
                            <span className="text-slate-400 ml-2">{a.userEmail}</span>
                            <span className="text-caption text-slate-300 ml-1">#{a.applicationId.slice(0, 8)}</span>
                          </button>
                        ))}
                      </div>,
                      document.body
                    )}
                  </>
                )}
              </div>

              {/* Title */}
              <div className="space-y-1.5">
                <label className="text-caption font-bold text-slate-500 uppercase">Title *</label>
                <input
                  type="text"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="Brief issue title..."
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-blue-400"
                />
              </div>

              {/* Description */}
              <div className="space-y-1.5">
                <label className="text-caption font-bold text-slate-500 uppercase">Description *</label>
                <div className="relative">
                  <textarea
                    value={newDescription}
                    onChange={(e) => setNewDescription(e.target.value)}
                    placeholder="Detailed description of the issue..."
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-blue-400 resize-none min-h-[80px]"
                    rows={3}
                  />
                  <div className="absolute top-1 right-1">
                    <ImproveTextDropdown
                      getValue={() => newDescription}
                      onImprove={(v) => setNewDescription(v)}
                      isHtml={false}
                      context="ticket description"
                    />
                  </div>
                </div>
              </div>

              {/* Priority */}
              <div className="space-y-1.5">
                <label className="text-caption font-bold text-slate-500 uppercase">Priority</label>
                <select
                  value={newPriority}
                  onChange={(e) => setNewPriority(e.target.value as TicketPriority)}
                  className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs font-bold outline-none focus:border-blue-400 cursor-pointer"
                >
                  {(['low', 'medium', 'high', 'urgent'] as TicketPriority[]).map(p => (
                    <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                  ))}
                </select>
              </div>

              {/* Assignment */}
              <div className="space-y-1.5">
                <label className="text-caption font-bold text-slate-500 uppercase">Assign To</label>
                <SearchableSelect
                  value={newAssignee}
                  onChange={(v) => setNewAssignee(v)}
                  options={(allAdmins || []).map(a => ({
                    value: `${a.uid}|${a.name || a.email}`,
                    label: a.name || a.email,
                  }))}
                  placeholder="Search assignees…"
                  clearLabel="— Unassigned —"
                  emptyLabel="No team members found"
                />
              </div>
            </div>
            <div className="px-5 py-4 border-t border-slate-200 flex items-center justify-end gap-2 shrink-0">
              <button onClick={() => setShowCreate(false)} className="px-3 py-1.5 border border-slate-200 text-caption font-bold rounded-lg text-slate-500 hover:bg-slate-50 transition-all cursor-pointer">
                Cancel
              </button>
              <button
                onClick={handleCreateTicket}
                disabled={!newTitle.trim() || !newDescription.trim() || !newAppId || creating}
                className="px-4 py-1.5 btn-primary  disabled:bg-slate-300 text-white text-caption font-bold rounded-xl transition-all cursor-pointer disabled:cursor-not-allowed flex items-center gap-1.5"
              >
                {creating ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                Create Ticket
              </button>
            </div>
          </div>
        </div>,
        document.body
        )}

    </div>
  );
}

/* ── Action Popover (portal-rendered inline popup) ── */

interface ActionPopoverProps {
  type: 'assign' | 'priority' | 'status';
  ticket: Ticket;
  anchorRect: DOMRect | null;
  allAdmins: { uid: string; name: string; email: string }[];
  onUpdate: (ticketId: string, updates: Partial<Ticket>) => Promise<void>;
  onClose: () => void;
}

function ActionPopover({ type, ticket, anchorRect, allAdmins, onUpdate, onClose }: ActionPopoverProps) {
  // All hooks at top level (Rules of Hooks compliance)
  const [selected, setSelected] = React.useState(ticket.assignedTo || '');
  const popoverRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // Reset selected when ticket changes (new popover opened)
  React.useEffect(() => { setSelected(ticket.assignedTo || ''); }, [ticket.ticketId, ticket.assignedTo]);

  const x = anchorRect ? Math.min(anchorRect.left, window.innerWidth - 220) : 0;
  const y = anchorRect ? anchorRect.bottom + 6 : 0;

  if (type === 'assign') {
    return (
      <div ref={popoverRef} className="fixed z-[90] bg-white border border-slate-200 rounded-2xl shadow-2xl p-3 min-w-[220px]"
        style={{ top: y, left: x }}>
        <div className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-2">Assign to</div>
        <div className="space-y-0.5 max-h-[200px] overflow-y-auto">
          <button onClick={() => onUpdate(ticket.ticketId, { assignedTo: null, assignedToName: null })}
            className={`w-full text-left px-2.5 py-2 text-xs font-semibold rounded-lg transition-all cursor-pointer ${selected === '' ? 'bg-brand-active' : 'text-slate-600 hover:bg-slate-50'}`} type="button">
            — Unassigned —
          </button>
          {allAdmins.map(a => (
            <button key={a.uid}
              onClick={() => { setSelected(a.uid); onUpdate(ticket.ticketId, { assignedTo: a.uid, assignedToName: a.name || a.email }); }}
              className={`w-full text-left px-2.5 py-2 text-xs font-semibold rounded-lg transition-all cursor-pointer ${selected === a.uid ? 'bg-brand-active' : 'text-slate-600 hover:bg-slate-50'}`} type="button">
              {a.name || a.email}
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (type === 'priority') {
    const priorities: { value: TicketPriority; label: string }[] = [
      { value: 'urgent', label: 'Urgent' }, { value: 'high', label: 'High' },
      { value: 'medium', label: 'Medium' }, { value: 'low', label: 'Low' },
    ];
    return (
      <div ref={popoverRef} className="fixed z-[90] bg-white border border-slate-200 rounded-2xl shadow-2xl p-3 min-w-[180px]"
        style={{ top: y, left: x }}>
        <div className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-2">Set Priority</div>
        <div className="space-y-0.5">
          {priorities.map(p => (
            <button key={p.value} onClick={() => onUpdate(ticket.ticketId, { priority: p.value })}
              className={`w-full text-left px-2.5 py-2 text-xs font-semibold rounded-lg transition-all cursor-pointer ${ticket.priority === p.value ? 'bg-brand-active' : 'text-slate-600 hover:bg-slate-50'}`} type="button">
              {p.label}
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (type === 'status') {
    const statuses: { value: TicketStatus; label: string }[] = [
      { value: 'open', label: 'Open' }, { value: 'in_progress', label: 'In Progress' },
      { value: 'pending', label: 'Pending' }, { value: 'resolved', label: 'Resolved' }, { value: 'closed', label: 'Closed' },
    ];
    return (
      <div ref={popoverRef} className="fixed z-[90] bg-white border border-slate-200 rounded-2xl shadow-2xl p-3 min-w-[180px]"
        style={{ top: y, left: x }}>
        <div className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-2">Change Status</div>
        <div className="space-y-0.5">
          {statuses.map(s => (
            <button key={s.value} onClick={() => onUpdate(ticket.ticketId, { status: s.value })}
              className={`w-full text-left px-2.5 py-2 text-xs font-semibold rounded-lg transition-all cursor-pointer ${ticket.status === s.value ? 'bg-brand-active' : 'text-slate-600 hover:bg-slate-50'}`} type="button">
              {s.label}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return null;
}
